import classNames from 'classnames';
import jQuery from 'jquery';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';

import {
  ActionHeader,
  BlockView,
  Loader,
  Pagination,
  Price,
  Table,
} from 'components';

import SystemInfoModal from 'views/systems/info-modal/container';
import defaultThumb from 'img/logo/default-thumb.png';
import systemFiltersConfig from 'utils/system-filters-config';

export default class SystemsOverview extends Component {

  /* Lifecycle */

  constructor(props) {
    super(props);

    this.COLUMNS = [
      {
        accessor: 'schema',
        cell: row => this.generateSchemaCell(row),
        hidden: true,
        modifier: 'shrink',
      }, {
        accessor: 'name',
        cell: row => this.generateNameCell(row),
        modifier: 'grow',
      }, {
        accessor: 'price',
        cell: row => this.generatePriceCell(row),
        modifier: 'align-right',
      },
    ];

    this.SORT_OPTIONS = [
      {
        label: props.intl.messages['systems.sorting.nameAsc'],
        value: 'name',
      }, {
        label: props.intl.messages['systems.sorting.nameDesc'],
        value: '-name',
      }, {
        label: props.intl.messages['systems.sorting.priceAsc'],
        value: 'price',
      }, {
        label: props.intl.messages['systems.sorting.priceDesc'],
        value: '-price',
      },
    ];

    this.state = {
      allFilters: null,
      pages: 1,
      systems: null,
      viewType: localStorage.getItem(`${'remeha-config'}-view-type`) || 'table',
      visibleFilters: null,
    };

    // Default config
    this.filterConfig = {
      heat_pump_energy: [],
      heating: null,
      heat_pump_series: [],
      // ...props.systems.filters, // Stored filter config TODO Needs some extra fixing for checkboxes to be checked
    };

    this.sorting = {
      currentPage: props.systems.sorting.page,
      pageSize: props.systems.sorting.per_page,
      selected: {
        label: this.SORT_OPTIONS.find(item => item.value === props.systems.sorting.sort).label,
        value: props.systems.sorting.sort,
      },
    };

    this.onComplete = this.onComplete.bind(this);
    this.paginationHandler = this.paginationHandler.bind(this);
    this.sortHandler = this.sortHandler.bind(this);
    this.tableRowHandler = this.tableRowHandler.bind(this);
    this.viewTypeHandler = this.viewTypeHandler.bind(this);
  }

  componentDidMount() {
    const { filters, systems } = this.props;

    if (filters.data) {
      this.formatAllFilters(filters);
    }

    if (systems.data) {
      this.formatSystemData(systems);
    }

    this.fetchSystemsData();
    this.fetchFiltersData();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.systems.data) {
      const oldSystems = JSON.stringify(this.props.systems.data);
      const newSystems = JSON.stringify(nextProps.systems.data);

      if (newSystems !== oldSystems) {
        this.formatSystemData(nextProps.systems);
        jQuery('.view__content').scrollTop(0);
      }
    }

    if (nextProps.filters.data) {
      const oldFilters = JSON.stringify(this.props.filters.data);
      const newFilters = JSON.stringify(nextProps.filters.data);

      if (newFilters !== oldFilters) {
        this.formatAllFilters(nextProps.filters);
      }
    }
  }

  /* Handlers */

  checkboxHandler(value, name, filter) {
    // See 'utils/system-filters-config.js' for all possible scenarios & extra info
    // Updating the config to hide/show extra filters based on selection
    if (name === 'all electric') {
      if (value && !this.filterConfig.heat_pump_series) {
        this.filterConfig.heat_pump_series = [];
      }
    } else if (name === 'hybride') {
      if (value) {
        this.filterConfig.kettle_type = null;

        if (!this.filterConfig.heat_pump_series) {
          this.filterConfig.heat_pump_series = [];
        }
      } else {
        delete this.filterConfig.kettle_type;
        delete this.filterConfig.kettle_series;
        delete this.filterConfig.cw_class;
        delete this.filterConfig.gas_connection_type;
      }
    }

    // Actual updating of the filter config
    if (value) {
      this.filterConfig[filter.name].push(name);
    } else {
      const index = this.filterConfig[filter.name].indexOf(name);

      if (index > -1) {
        this.filterConfig[filter.name].splice(index, 1);
      }
    }

    // Reset page to 1
    this.sorting.currentPage = 1;

    this.updateVisibleFilters();
    this.fetchSystemsData();
  }

  paginationHandler(page) {
    this.sorting.currentPage = page;

    this.fetchSystemsData();
  }

  selectHandler(value, filter) {
    if (value) {
      if (value.value === 'cv handhaven') {
        delete this.filterConfig.kettle_series;
        delete this.filterConfig.cw_class;
        delete this.filterConfig.gas_connection_type;
      } else if (value.value === 'nieuwe cv') {
        this.filterConfig.kettle_series = null;
        this.filterConfig.cw_class = null;
        this.filterConfig.gas_connection_type = null;
      }
    } else if (!value && filter.name === 'kettle_type') {
      delete this.filterConfig.kettle_series;
      delete this.filterConfig.cw_class;
      delete this.filterConfig.gas_connection_type;
    }

    this.filterConfig = {
      ...this.filterConfig,
      [filter.name]: value,
    };

    // Reset page to 1
    this.sorting.currentPage = 1;

    this.updateVisibleFilters();
    this.fetchSystemsData();
  }

  sortHandler(value) {
    this.sorting.selected = value;

    this.fetchSystemsData();
  }

  tableRowHandler({ id }) {
    this.props.systemGet(id).then((response) => {
      this.openSystemDetailModal(response);
    });
  }

  viewTypeHandler(value) {
    this.setState({
      viewType: value,
    }, () => {
      try {
        localStorage.setItem(`${'remeha-config'}-view-type`, value);
      } catch (err) {
        console.warn('Unable to set to localStorage', err); // eslint-disable-line
      }
    });
  }

  /* Methods */

  fetchFiltersData() {
    this.props.filtersGetAll();
  }

  fetchSystemsData() {
    const filters = {};
    const filterKeys = Object.keys(this.filterConfig);

    // Format the dropdown/select values & filter out empties
    filterKeys.forEach((filterKey) => {
      if (this.filterConfig[filterKey]) {
        if (!Array.isArray(this.filterConfig[filterKey])) {
          filters[filterKey] = this.filterConfig[filterKey].value;
        } else if (this.filterConfig[filterKey].length) {
          filters[filterKey] = this.filterConfig[filterKey];
        }
      }
    });

    const sorting = {
      page: this.sorting.currentPage,
      per_page: this.sorting.pageSize,
      sort: this.sorting.selected.value,
    };

    this.props.systemsGetAll(sorting, filters, this.filterConfig);
  }

  formatAllFilters(filters) {
    // Generates a list of all filters with possible options (based on the util config)
    const filterKeys = Object.keys(filters.data);
    const allFilters = {};

    filterKeys.forEach((filterKey) => {
      const newFilter = {
        ...systemFiltersConfig[filterKey],
        name: filterKey,
        options: [],
      };

      const filterOptions = Object.keys(filters.data[filterKey]).sort((a, b) => { return a - b; });

      if (filterOptions.length) {
        filterOptions.forEach((filterOption) => {
          newFilter.options.push({
            label: filters.data[filterKey][filterOption],
            value: filterOption,
          });
        });
      }

      allFilters[filterKey] = newFilter;
    });

    this.setState({
      allFilters,
    }, () => {
      this.updateVisibleFilters();
    });
  }

  formatSystemData(systemData) {
    const systems = systemData.data.map((system) => {
      return {
        ...system,
        onClick: this.tableRowHandler,
        schema: system.sketch || defaultThumb,
      };
    });

    const total = systemData.total;
    const pages = total % this.sorting.pageSize === 0 ?
      Math.floor(total / this.sorting.pageSize) :
      Math.floor(total / this.sorting.pageSize) + 1;

    this.setState({
      systems,
      pages,
    });
  }

  generateCheckboxes(filter) {
    const options = filter.options.map((option) => {
      const id = `checkbox_${option.value}`;

      return (
        <label className="checkbox systems__filter-checkbox" htmlFor={id} key={id}>
          <input
            className="checkbox__square"
            disabled={this.props.systems.get.loading}
            id={id}
            onChange={evt => this.checkboxHandler(evt.target.checked, option.value, filter)}
            type="checkbox"
          />
          <div className={classNames('checkbox__label', { 'is-disabled': this.props.systems.get.loading })}>
            {option.label}
          </div>
        </label>
      );
    });

    return (
      <div className="systems__filter" key={filter.name}>
        <div className="systems__filter-title">
          <FormattedMessage id={filter.label} />
        </div>
        {options}
      </div>
    );
  }

  generateDropdown(filter) {
    return (
      <div className="systems__filter" key={filter.name}>
        <div className="systems__filter-title">
          <FormattedMessage id={filter.label} />
        </div>
        <div className="systems__filter-select">
          <Select
            arrowRenderer={this.renderArrowSelect}
            className="Select--md Select--font-xs Select--font-bold"
            disabled={this.props.systems.get.loading}
            name="filter-dropdown"
            onChange={value => this.selectHandler(value, filter)}
            options={filter.options}
            placeholder={this.props.intl.messages['components.select.placeholder']}
            searchable={false}
            value={filter.selected}
          />
        </div>
      </div>
    );
  }

  generateNameCell(row) {
    return (
      <div className="table-body__cell__content table-body__cell__content--multiline">
        <div>{row.name}</div>
        <div className="u-color-gray u-margin-top-sm">{row.chapter}</div>
      </div>
    );
  }

  generatePriceCell(row) {
    return (
      <div className="table-body__cell__content table-body__cell__content--align-right">
        <Price value={row.price} vatExcluded />
      </div>
    );
  }

  generateSchemaCell(row) {
    return (
      <div className="table-body__cell__image">
        <img src={row.sketch || defaultThumb} alt={row.name} />
      </div>
    );
  }

  onComplete() {
    this.props.appModalClose('all');
  }

  openSystemDetailModal(system = null) {
    this.props.appModalOpen({
      component: (
        <SystemInfoModal
          onSubmit={this.onComplete}
          system={system}
        />
      ),
      price: system.attributes.price,
      scrollDisabled: true,
      title: system.attributes.name,
      type: 'large',
    });
  }

  updateVisibleFilters() {
    const { allFilters } = this.state;

    // Put filters in correct order (using util system-filters-config)
    const filterConfigKeys = Object.keys(this.filterConfig);
    const orderedFilterConfigKeys = Object.keys(systemFiltersConfig).filter(element => filterConfigKeys.includes(element));

    const visibleFilters = orderedFilterConfigKeys.map((filterKey) => {
      return {
        ...allFilters[filterKey],
        selected: this.filterConfig[filterKey],
      };
    });

    this.setState({
      visibleFilters,
    });
  }

  /* Render */

  render() {
    return (
      <div className="view">
        {this.renderHeader()}
        <div className="systems">
          {this.renderFilters()}
          <div className="view__content systems__overview">
            <div className="container">
              <div className="row">
                <div className="gr-12">
                  <div className="offset">{this.renderActionHeader()}</div>
                  <div className="offset">{this.renderContent()}</div>
                  <div className="offset offset--sm">{this.renderPagination()}</div>
                  <div className="offset" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderFilters() {
    const { visibleFilters } = this.state;

    let content = null;

    if (visibleFilters && visibleFilters.length) {
      content = visibleFilters.map((filter) => {
        if (filter.type === 'dropdown') {
          return this.generateDropdown(filter);
        } else if (filter.type === 'checkboxes') {
          return this.generateCheckboxes(filter);
        }

        return null;
      });
    }

    return (
      <div className="systems__filters">
        {content}
      </div>
    );
  }

  renderHeader() {
    return (
      <div className="view__header">
        <div className="header">
          <div className="header__inner">
            <div className="align align--fluid align--gutter-xs">
              <div className="align__cell align__cell--shrink">
                <h1 className="header__title header__title--no-spacing">
                  <FormattedMessage id="systems.title" />
                </h1>
              </div>
              <div className="align__cell align__cell--shrink">
                <Loader size="md" color="#FFD000" visible={this.props.filters.get.loading || this.props.systems.get.loading} />
              </div>
              <div className="align__cell" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderActionHeader() {
    return (
      <ActionHeader
        onSortChange={this.sortHandler}
        onViewChange={this.viewTypeHandler}
        selectedSortOption={this.sorting.selected}
        sortOptions={this.SORT_OPTIONS}
        viewType={this.state.viewType}
      />
    );
  }

  renderContent() {
    const { systems, viewType } = this.state;

    if (!systems) {
      return null;
    }

    let content = null;

    if (viewType === 'table') {
      content = this.renderTable();
    } else if (viewType === 'blocks') {
      content = this.renderBlockview();
    }

    return content;
  }

  renderBlockview() {
    const { systems } = this.state;

    const blocks = systems.map((system) => {
      return {
        ...system,
        onItemClick: this.tableRowHandler,
        subtitle: system.chapter,
        title: system.name,
      };
    });

    return (
      <BlockView data={blocks} />
    );
  }

  renderTable() {
    return (
      <Table
        columns={this.COLUMNS}
        defaultSorted={this.sorting.selected}
        noDataMessage={this.props.intl.messages['systems.overview.noData']}
        rows={this.state.systems}
        rowClick={this.tableRowHandler}
        showHeader={false}
      />
    );
  }

  renderPagination() {
    const { systems, pages } = this.state;

    if (systems && systems.length) {
      return (
        <Pagination
          amountPages={pages}
          currentPage={this.sorting.currentPage}
          onChange={this.paginationHandler}
        />
      );
    }

    return null;
  }

  renderArrowSelect(params) {
    const icon = params.isOpen ? 'up' : 'down';

    return (
      <span className={`icon icon-chevron-${icon} icon--xxs`} />
    );
  }
}

SystemsOverview.propTypes = {
  appModalClose: PropTypes.func,
  appModalOpen: PropTypes.func,
  intl: PropTypes.object,
  filters: PropTypes.object,
  filtersGetAll: PropTypes.func,
  systemGet: PropTypes.func,
  systems: PropTypes.object,
  systemsGetAll: PropTypes.func,
};



// WEBPACK FOOTER //
// ./js/views/systems/overview/index.jsx